import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '@plato/ui/util/classes';
import React, { createContext, useCallback, useContext, useId } from 'react';
import type { RowComponentProps } from 'react-window';
import { List } from 'react-window';
import { toLocaleString } from '../../lib/currency-helpers.ts';
import { useResolvedLocale } from '../../lib/hooks/locale.ts';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import Spinner from '../ui/spinner.tsx';

export type FilterOptionsListProps = {
    children?:
        | React.ReactElement<SearchProps | CheckboxGroupProps>
        | React.ReactElement<SearchProps | CheckboxGroupProps>[];
};

export function FilterOptionsList({ children }: FilterOptionsListProps) {
    const header = React.Children.toArray(children).find((child) => {
        return React.isValidElement(child) && child.type === FilterOptionsList.Search;
    });

    const checkboxGroup = React.Children.toArray(children).find((child) => {
        return React.isValidElement(child) && child.type === FilterOptionsList.CheckboxGroup;
    });

    return (
        <div className="w-full">
            <div className="flex h-[52px] flex-col border-b border-neutral-200 px-3 py-2">{header}</div>
            <div className="flex h-[calc(100%-52px)] flex-col overflow-y-hidden px-3 py-2">
                {checkboxGroup}
            </div>
        </div>
    );
}

type SearchProps = {
    placeholder?: string;
    onChange: (value: string) => void;
};

function Search({ placeholder, onChange }: SearchProps) {
    return (
        <Input
            placeholder={placeholder}
            leftIcon={MagnifyingGlassIcon}
            onChange={(e) => {
                onChange(e.target.value);
            }}
        />
    );
}

function LoadingSpinner() {
    return (
        <div className="flex h-20 w-full items-center justify-center">
            <Spinner />
        </div>
    );
}

export const CheckboxContext = createContext<CheckboxContextType | undefined>(undefined);

type CheckboxContextType = {
    checkedValues: string[];
    toggleCheckbox: (value: string) => void;
};

type CheckboxGroupProps = {
    children?: React.ReactNode | React.ReactNode[];
    values: string[];
    onChange: (values: string[]) => void;
    itemHeight?: number;
};

export function CheckboxGroup({ children, values, onChange, itemHeight = 26, ...props }: CheckboxGroupProps) {
    const scrollRefSelect = React.useRef<HTMLDivElement>(null);

    const toggleCheckbox = useCallback(
        (value: string) => {
            if (values.includes(value)) {
                onChange(values.filter((item) => item !== value));
            } else {
                onChange([...values, value]);
            }
        },
        [values, onChange],
    );

    // Convert children to array for react-window
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const itemCount = childrenArray.length;

    // Row component for react-window
    const RowComponent = ({ index, style, ariaAttributes }: RowComponentProps) => (
        <div style={style} {...ariaAttributes}>
            {childrenArray[index]}
        </div>
    );

    return (
        <CheckboxContext.Provider value={{ checkedValues: values, toggleCheckbox }}>
            <div {...props} className="flex flex-col w-full overflow-hidden gap-2" ref={scrollRefSelect}>
                {itemCount > 0 && (
                    <List
                        rowComponent={RowComponent}
                        rowCount={itemCount}
                        rowHeight={itemHeight}
                        overscanCount={5}
                        rowProps={{}}
                        style={{ maxHeight: '40vh' }}
                    />
                )}
            </div>
        </CheckboxContext.Provider>
    );
}

type CheckboxProps = {
    value: string;
    label: string;
    count?: number;
};

function Checkbox({ value, label, count }: CheckboxProps) {
    const locale = useResolvedLocale();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- legacy code
    const { checkedValues, toggleCheckbox } = useContext(CheckboxContext)!;
    const isChecked = checkedValues.includes(value);
    const checkboxId = useId();

    return (
        <div className={cn('flex gap-2 items-center text-sm', 'rounded-sm hover:bg-cool-gray-25')}>
            <div className="flex items-center">
                &#8203;
                <input
                    className="outline-blue rounded-sm border border-neutral-300 accent-blue-500 ring-offset-neutral-300"
                    type="checkbox"
                    value={value}
                    checked={isChecked}
                    onChange={() => {
                        toggleCheckbox(value);
                    }}
                    aria-label={label}
                    aria-checked={isChecked}
                    id={checkboxId}
                />
            </div>
            <Label className="flex-1 text-neutral-700 text-sm truncate" htmlFor={checkboxId} title={label}>
                {label}
            </Label>
            {count ? (
                <div className="flex items-center justify-center border rounded-full border-neutral-200 px-2 py-1 text-xs">
                    <span>{toLocaleString(count, locale)}</span>
                </div>
            ) : null}
        </div>
    );
}

FilterOptionsList.Search = Search;
FilterOptionsList.LoadingSpinner = LoadingSpinner;
FilterOptionsList.CheckboxGroup = CheckboxGroup;
FilterOptionsList.Checkbox = Checkbox;
